import { profileKey } from "./profileSession";
import { WEEKDAYS, type DayKey, type WeekAvailability } from "../components/motion/availability-scheduler/types";

const STORAGE_KEY = "kontrataph:availability-recovery:v1";
export const AVAILABILITY_RETURN_PATH = "/worker-profile?section=availability";
type Owner = { id: number; activeContext: string | null };
export type AvailabilityDraft = { days: number[]; start: string; end: string;
  week?: WeekAvailability; savedSlotIds?: string[]; day?: DayKey };

function validDraft(value: unknown): value is AvailabilityDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as Record<string, unknown>;
  const validTime = (time: unknown) => typeof time === "string" && (time === "" || /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time));
  if (draft.week !== undefined) {
    const week = draft.week as Record<string, { enabled?: unknown; ranges?: unknown }>;
    if (!week || typeof week !== "object" || !Array.isArray(draft.savedSlotIds)
      || !draft.savedSlotIds.every(id => typeof id === "string")
      || !WEEKDAYS.some(item => item.key === draft.day)
      || !WEEKDAYS.every(({ key }) => typeof week[key]?.enabled === "boolean" && Array.isArray(week[key]?.ranges)
        && (week[key].ranges as unknown[]).every(value => {
          if (!value || typeof value !== "object") return false;
          const range = value as Record<string, unknown>;
          return typeof range.id === "string" && validTime(range.start) && validTime(range.end);
        }))) return false;
  }
  return Array.isArray(draft.days) && draft.days.length <= 7
    && draft.days.every(day => Number.isInteger(day) && day >= 0 && day <= 6)
    && new Set(draft.days).size === draft.days.length && validTime(draft.start) && validTime(draft.end);
}

/** Keep only this tab's unsubmitted hours, never a command to replay after login. */
export function saveAvailabilityDraft(owner: Owner, draft: AvailabilityDraft): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ scope: profileKey(owner), draft }));
    return true;
  } catch {
    // An earlier draft must not masquerade as the hours we could not retain.
    clearAvailabilityDraft();
    return false;
  }
}

/** A different account/profile cannot recover a prior owner's hours or return route. */
export function readAvailabilityDraft(owner: Owner): AvailabilityDraft | null {
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    if (stored && typeof stored === "object" && "scope" in stored && "draft" in stored
      && stored.scope === profileKey(owner) && validDraft(stored.draft)) return stored.draft;
    clearAvailabilityDraft();
  } catch { clearAvailabilityDraft(); }
  return null;
}

export function clearAvailabilityDraft(): void {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* Unavailable storage must not prevent editing or saving. */ }
}
